import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { adminAPI, usersAPI, peoplePlatformAPI, positionsAPI } from '@/services';
import { mapPeopleListResponse } from '../models/memberModel';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import SearchAndFilterBar from '../components/SearchAndFilterBar';
import PageHeader from '../components/PageHeader';
import Pagination from '../components/Pagination';
import TableRowActions from '../components/TableRowActions';
import UiSurface from '../components/UiSurface';
import {
  isOrgAdminRole,
  isOrgManagerRole,
  formatIamRoleLabel,
  normalizeUserRoleForForm,
  isMemberEffectiveRole,
  resolveUserRoleFromPosition,
  primaryRoleKeyFromPosition,
} from '../utils/iamRoles';
import { searchFieldsMatch } from '../utils/searchText';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import { parseTabularFile } from '../utils/tabularImport';
import { downloadUsersImportTemplate } from '../utils/importTemplates';
import ModalShell from '../components/ModalShell';
import { staffUsersBasePath } from '../utils/portalAdminNav';
import { adminConsoleUrl, ADMIN_CONSOLE_URL } from '../config/platform';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const UsersManagement = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canResetPassword = hasPermission('users.edit');
  const navigate = useNavigate();
  const location = useLocation();
  const usersBasePath = staffUsersBasePath(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [showModal, setShowModal] = useState(false);
  const [importingUsers, setImportingUsers] = useState(false);
  const usersImportInputRef = useRef(null);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPwModalOpen, setResetPwModalOpen] = useState(false);
  const [resetPwTargets, setResetPwTargets] = useState([]);
  const [resetPwStep, setResetPwStep] = useState('form');
  const [resetPwOutcome, setResetPwOutcome] = useState(null);
  const [resetPwShared, setResetPwShared] = useState(true);
  const [resetPwSharedValue, setResetPwSharedValue] = useState('');
  const [resetPwSharedConfirm, setResetPwSharedConfirm] = useState('');
  const [resetPwMustChange, setResetPwMustChange] = useState(true);
  const [resetPwSaving, setResetPwSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    username: '',
    email: '',
    password: '',
    role: 'participant',
    member_id: '',
    position_id: '',
    is_active: true
  });
  const [projectPositions, setProjectPositions] = useState([]);
  const projectId = useMemo(() => {
    const fromStorage = String(localStorage.getItem('project_id') || '').trim();
    if (fromStorage) return fromStorage;
    const fromUser = String(user?.project_id || '').trim();
    if (fromUser) return fromUser;
    try {
      const token = String(localStorage.getItem('token') || localStorage.getItem('access_token') || '').trim();
      if (!token || token.split('.').length < 2) return '';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return String(payload?.project_id || '').trim();
    } catch {
      return '';
    }
  }, [user?.project_id]);

  const { roles: assignableRoles } = useAssignableRoles();
  const roleFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'Tous les rôles' },
      ...assignableRoles.map((r) => ({ value: r.key, label: r.label })),
    ],
    [assignableRoles]
  );
  const projectAdminUrl = useMemo(
    () => (projectId ? adminConsoleUrl(`/projects/${encodeURIComponent(projectId)}`) : ''),
    [projectId]
  );
  const projectsListUrl = useMemo(() => adminConsoleUrl('/projects'), []);
  const positionLabelById = useMemo(() => {
    const m = new Map();
    for (const p of projectPositions || []) {
      const id = String(p?.id || '').trim();
      if (!id) continue;
      m.set(id, String(p?.label || p?.key || id));
    }
    return m;
  }, [projectPositions]);
  const roleColumnLabelForUser = (user) =>
    positionLabelById.get(String(user?.position_id || '').trim()) || '—';

  const usersTotalPages = Math.max(1, Math.ceil(usersTotal / itemsPerPage));
  const renderUsersPagination = (placement) => (
    <Pagination
      placement={placement}
      currentPage={usersPage}
      totalPages={usersTotalPages}
      totalItems={usersTotal}
      itemsPerPage={itemsPerPage}
      onPageChange={(page) => {
        setUsersPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
      onPageSizeChange={(n) => {
        setItemsPerPage(n);
        setUsersPage(1);
      }}
    />
  );

  useEffect(() => {
    loadData();
  }, [usersPage, itemsPerPage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await positionsAPI.getList();
        if (!cancelled) setProjectPositions(res.data?.positions || []);
      } catch {
        if (!cancelled) setProjectPositions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, selectedRole, selectedStatus]);

  useEffect(() => {
    // Garde uniquement les IDs encore visibles dans la liste courante
    const visibleIds = new Set(filteredUsers.map((u) => u.id));
    setSelectedUsers((prev) => prev.filter((id) => visibleIds.has(id)));
  }, [filteredUsers]);

  const filterUsers = () => {
    let filtered = users;

    // Filtre par rôle (IAM : org_admin / org_manager regroupent les anciens codes)
    if (selectedRole !== 'all') {
      if (selectedRole === 'org_admin') {
        filtered = filtered.filter((user) => isOrgAdminRole(user.role));
      } else if (selectedRole === 'org_manager') {
        filtered = filtered.filter((user) => isOrgManagerRole(user.role));
      } else {
        filtered = filtered.filter((user) => user.role === selectedRole);
      }
    }

    // Filtre par statut
    if (selectedStatus === 'active') {
      filtered = filtered.filter(user => user.is_active === true);
    } else if (selectedStatus === 'inactive') {
      filtered = filtered.filter(user => user.is_active === false);
    }

    // Recherche par nom, username, email ou téléphone
    if (searchQuery.trim()) {
      filtered = filtered.filter((user) =>
        searchFieldsMatch(searchQuery, [user.name, user.username, user.email, user.phone])
      );
    }

    setFilteredUsers(filtered);
  };

  // Ouvrir le modal d'édition si le paramètre edit est présent dans l'URL
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (editId && users.length > 0) {
      const userToEdit = users.find(u => u.id === editId);
      if (userToEdit) {
        setEditingUser(userToEdit);
        setFormData({
          name: userToEdit.name || '',
          phone: userToEdit.phone || '',
          username: userToEdit.username || '',
          email: userToEdit.email || '',
          password: '',
          role: normalizeUserRoleForForm(userToEdit.role) || 'participant',
          member_id: userToEdit.member_id || '',
          is_active: userToEdit.is_active !== undefined ? userToEdit.is_active : true
        });
        setShowModal(true);
        // Retirer le paramètre de l'URL
        setSearchParams({});
      }
    }
  }, [searchParams, users, setSearchParams]);

  const formatError = (errorDetail) => {
    // Si c'est une chaîne, la retourner directement
    if (typeof errorDetail === 'string') {
      return errorDetail;
    }
    // Si c'est un tableau d'erreurs de validation Pydantic
    if (Array.isArray(errorDetail)) {
      return errorDetail.map(err => {
        const field = err.loc ? err.loc.slice(1).join('.') : 'champ';
        return `${field}: ${err.msg}`;
      }).join(', ');
    }
    // Si c'est un objet, essayer d'extraire le message
    if (typeof errorDetail === 'object' && errorDetail !== null) {
      return errorDetail.msg || errorDetail.message || JSON.stringify(errorDetail);
    }
    return 'Erreur lors de l\'opération';
  };

  const getFriendlyUserConflictMessage = (err) => {
    if (Number(err?.response?.status) !== 409) return null;
    return 'Un utilisateur existe déjà avec cet email ou ce numéro de téléphone.';
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {
        limit: itemsPerPage,
        skip: (usersPage - 1) * itemsPerPage
      };
      const [usersResponse, membersResponse] = await Promise.all([
        usersAPI.getAll(params),
        peoplePlatformAPI.list({ limit: 2000, skip: 0 })
      ]);
      
      // Gérer les deux formats de réponse
      if (Array.isArray(usersResponse.data)) {
        setUsers(usersResponse.data);
        setFilteredUsers(usersResponse.data);
        setUsersTotal(usersResponse.data.length);
      } else {
        setUsers(usersResponse.data.users || []);
        setFilteredUsers(usersResponse.data.users || []);
        setUsersTotal(usersResponse.data.total || 0);
      }
      
      setMembers(mapPeopleListResponse(membersResponse.data));
    } catch (err) {
      const errorMessage = err.response?.data?.detail 
        ? formatError(err.response.data.detail)
        : 'Erreur lors du chargement des données';
      error(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't send empty password
        }
        await usersAPI.update(editingUser.id, updateData);
        success('Utilisateur mis à jour avec succès');
      } else {
        // Create user
        const isMember = isMemberEffectiveRole(formData.role) || formData.member_id;

        // Pour les membres, le mot de passe sera généré automatiquement
        if (!isMember && !formData.password) {
          error('Le mot de passe est requis pour les comptes non-membres');
          return;
        }
        
        // Pour les membres, le téléphone est requis (sera utilisé comme username si username non fourni)
        if (isMember && !formData.phone) {
          error('Le numéro de téléphone est requis pour créer un compte membre');
          return;
        }
        
        // Préparer les données : ne pas envoyer password si c'est un membre
        const createData = { ...formData };
        if (isMember) {
          delete createData.password; // Le backend générera le mot de passe automatiquement
        }
        
        const response = await usersAPI.create(createData);
        const createdUser = response.data;
        
        success('Utilisateur créé avec succès');
        setShowModal(false);
        resetForm();
        
        // Si c'est un compte membre en attente de validation, rediriger vers la page de détails
        if (isMember && createdUser.validation_status === 'pending_validation') {
          navigate(`${usersBasePath}/${createdUser.id}`);
        } else {
          // Retirer le paramètre edit de l'URL si présent
          if (searchParams.get('edit')) {
            setSearchParams({});
          }
          loadData();
        }
      }
    } catch (err) {
      const errorMessage = getFriendlyUserConflictMessage(err)
        || (err.response?.data?.detail ? formatError(err.response.data.detail) : 'Erreur lors de l\'opération');
      error(errorMessage);
      console.error(err);
    }
  };

  const handleImportUsersFile = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      setImportingUsers(true);
      const rows = await parseTabularFile(file);
      if (!Array.isArray(rows) || rows.length === 0) {
        error('Le fichier est vide ou invalide');
        return;
      }
      const { data } = await adminAPI.importData('users', rows, 'json');
      const created = Number(data?.created_count || 0);
      const errorsCount = Array.isArray(data?.errors) ? data.errors.length : 0;
      if (errorsCount > 0) {
        const firstError = data.errors[0]?.error || 'Erreur non detaillee';
        success(`Import termine: ${created} utilisateur(s), ${errorsCount} erreur(s) (1ere: ${firstError}).`);
      } else {
        success(`Import termine: ${created} utilisateur(s).`);
      }
      await loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail
        ? formatError(err.response.data.detail)
        : 'Erreur lors de l import des utilisateurs';
      error(errorMessage);
    } finally {
      setImportingUsers(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      phone: user.phone || '',
      username: user.username || '',
      email: user.email || '',
      password: '',
      role: normalizeUserRoleForForm(user.role) || 'participant',
      member_id: user.member_id || '',
      position_id: user.position_id || '',
      is_active: user.is_active !== undefined ? user.is_active : true
    });
    usersAPI.getById(user.id).then((res) => {
      const pid = res.data?.position_id;
      if (pid) setFormData((prev) => ({ ...prev, position_id: String(pid) }));
    }).catch(() => {});
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    const confirmed = await confirm({
      title: 'Supprimer l\'utilisateur',
      message: 'Êtes-vous sûr de vouloir supprimer cet utilisateur ? Cette action est irréversible.',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      await usersAPI.delete(userId);
      success('Utilisateur supprimé avec succès');
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail 
        ? formatError(err.response.data.detail)
        : 'Erreur lors de la suppression';
      error(errorMessage);
      console.error(err);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;

    const confirmed = await confirm({
      title: 'Supprimer les utilisateurs sélectionnés',
      message: `Êtes-vous sûr de vouloir supprimer ${selectedUsers.length} utilisateur(s) ? Cette action est irréversible.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
    });

    if (!confirmed) return;

    try {
      await Promise.all(selectedUsers.map((id) => usersAPI.delete(id)));
      success(`${selectedUsers.length} utilisateur(s) supprimé(s) avec succès`);
      setSelectedUsers([]);
      loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.detail
        ? formatError(err.response.data.detail)
        : 'Erreur lors de la suppression multiple';
      error(errorMessage);
      console.error(err);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleSelectAllUsers = () => {
    const visibleIds = filteredUsers.map((u) => u.id);
    if (visibleIds.length === 0) return;
    const allSelected = visibleIds.every((id) => selectedUsers.includes(id));
    setSelectedUsers(allSelected ? [] : visibleIds);
  };

  const openResetPasswordModal = (userIds) => {
    const ids = (Array.isArray(userIds) ? userIds : [userIds]).map((x) => String(x || '').trim()).filter(Boolean);
    if (!ids.length) return;
    setResetPwTargets(ids);
    setResetPwStep('form');
    setResetPwOutcome(null);
    setResetPwShared(true);
    setResetPwSharedValue('');
    setResetPwSharedConfirm('');
    setResetPwMustChange(true);
    setResetPwModalOpen(true);
  };

  const closeResetPasswordModal = () => {
    setResetPwModalOpen(false);
    setResetPwSaving(false);
    setResetPwOutcome(null);
    setResetPwStep('form');
  };

  const handleSubmitResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPwTargets.length) return;
    if (resetPwShared) {
      const p = resetPwSharedValue.trim();
      const c = resetPwSharedConfirm.trim();
      if (p.length < 8) {
        error('Le mot de passe doit contenir au moins 8 caractères.');
        return;
      }
      if (p !== c) {
        error('Les deux saisies du mot de passe ne correspondent pas.');
        return;
      }
    }
    try {
      setResetPwSaving(true);
      const body = {
        user_ids: resetPwTargets,
        must_change_password: resetPwMustChange,
      };
      if (resetPwShared) body.password = resetPwSharedValue.trim();
      const { data } = await usersAPI.resetPasswords(body);
      setResetPwOutcome(data);
      setResetPwStep('done');
      const okCount = (data?.results || []).filter((r) => r.ok).length;
      const n = resetPwTargets.length;
      if (okCount === n) {
        success('Mots de passe mis à jour.');
      } else {
        success(`Réinitialisation partielle : ${okCount} / ${n} compte(s) mis à jour.`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail ? formatError(err.response.data.detail) : 'Échec de la réinitialisation.';
      error(msg);
    } finally {
      setResetPwSaving(false);
    }
  };

  const copyResetPasswordSummary = async () => {
    const list = resetPwOutcome?.results || [];
    const userById = new Map(users.map((u) => [String(u.id), u]));
    const lines = list
      .filter((r) => r.ok)
      .map((r) => {
        const u = userById.get(String(r.user_id));
        const label = u?.username || u?.name || r.user_id;
        const pw =
          r.temporary_password ||
          (resetPwOutcome?.used_shared_password ? '(mot de passe commun défini)' : '—');
        return `${label}\t${pw}`;
      });
    if (!lines.length) {
      error('Rien à copier.');
      return;
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      success('Copié dans le presse-papiers.');
    } catch {
      error('Impossible de copier (presse-papiers indisponible).');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      username: '',
      email: '',
      password: '',
      role: 'participant',
      member_id: '',
      position_id: '',
      is_active: true
    });
    setEditingUser(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    // Retirer le paramètre edit de l'URL si présent
    if (searchParams.get('edit')) {
      setSearchParams({});
    }
  };

  const getRoleBadgeColor = (role) => {
    const r = role != null ? String(role).toLowerCase() : '';
    const colors = {
      admin: 'bg-entrea-panel text-purple-800',
      org_admin: 'bg-entrea-panel text-purple-800',
      organization_admin: 'bg-entrea-panel text-purple-800',
      manager: 'bg-amber-100 text-amber-800',
      org_manager: 'bg-amber-100 text-amber-800',
      executive: 'bg-blue-100 text-blue-800',
      président: 'bg-blue-100 text-blue-800',
      president: 'bg-blue-100 text-blue-800',
      finance_lead: 'bg-green-100 text-green-800',
      trésorier: 'bg-green-100 text-green-800',
      treasurer: 'bg-green-100 text-green-800',
      operations_lead: 'bg-yellow-100 text-yellow-800',
      secrétaire: 'bg-yellow-100 text-yellow-800',
      secretary: 'bg-yellow-100 text-yellow-800',
      participant: 'bg-gray-100 text-gray-800',
      membre: 'bg-gray-100 text-gray-800',
      member: 'bg-gray-100 text-gray-800',
    };
    return colors[r] || colors.participant;
  };

  const getMemberName = (memberId) => {
    if (!memberId) return 'Non lié';
    const member = members.find(m => m.id === memberId);
    return member ? member.name : 'Membre non trouvé';
  };

  const editingTechnicalIam = useMemo(
    () =>
      Boolean(
        editingUser &&
          (isOrgAdminRole(editingUser.role) ||
            isOrgManagerRole(editingUser.role) ||
            ['admin_project', 'saas_admin'].includes(String(editingUser.role || '').toLowerCase()))
      ),
    [editingUser]
  );

  /** Alignement automatique : la position projet dicte le rôle IAM (sauf comptes techniques). */
  useEffect(() => {
    if (!showModal) return;
    if (editingTechnicalIam) return;
    const pid = String(formData.position_id || '').trim();
    if (!pid) return;
    if (!Array.isArray(projectPositions) || projectPositions.length === 0) return;
    const derived = resolveUserRoleFromPosition(pid, projectPositions, formData.role || 'participant');
    if (!derived) return;
    if (String(derived).toLowerCase() === String(formData.role || '').toLowerCase()) return;
    setFormData((prev) => ({ ...prev, role: derived }));
  }, [showModal, formData.position_id, projectPositions, editingTechnicalIam]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <PageHeader
        title="Gestion des Utilisateurs"
        subtitle="Gérez tous les comptes utilisateurs du système"
        actions={
          <div className="flex items-center gap-2">
            <input
              ref={usersImportInputRef}
              type="file"
              accept=".csv,.txt,.xls,.xlsx"
              className="hidden"
              onChange={handleImportUsersFile}
            />
            <UiSurface id="users.import">
              <button
                type="button"
                onClick={() => usersImportInputRef.current?.click()}
                disabled={importingUsers}
                className="px-4 py-2 border border-entrea-royal text-entrea-royal rounded-lg hover:bg-entrea-sky/10 transition font-medium text-sm whitespace-nowrap disabled:opacity-60"
              >
                {importingUsers ? 'Import en cours...' : 'Importer utilisateurs'}
              </button>
            </UiSurface>
            <UiSurface id="users.import_template">
              <button
                type="button"
                onClick={downloadUsersImportTemplate}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm whitespace-nowrap"
              >
                Modèle CSV
              </button>
            </UiSurface>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm whitespace-nowrap"
            >
              + Nouvel Utilisateur
            </button>
          </div>
        }
      />


      {/* Barre de recherche et filtres */}
      <SearchAndFilterBar
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Rechercher par nom, username, email ou téléphone..."
        collapseSearchOnMobile
        mobileFiltersToggleLabel="Filtres"
        filters={[
          {
            key: 'role',
            label: 'Rôle',
            type: 'select',
            value: selectedRole,
            onChange: setSelectedRole,
            options: roleFilterOptions
          },
          {
            key: 'status',
            label: 'Statut',
            type: 'button-group',
            value: selectedStatus,
            onChange: setSelectedStatus,
            options: [
              { value: 'all', label: 'Tous' },
              { value: 'active', label: 'Actifs' },
              { value: 'inactive', label: 'Inactifs' }
            ]
          }
        ]}
      />

      {selectedUsers.length > 0 && (
        <div className="bg-entrea-panel border border-entrea-sky/30 rounded-lg px-4 py-2 flex items-center justify-between gap-3">
          <span className="text-sm text-entrea-navy font-medium">
            {selectedUsers.length} utilisateur(s) sélectionné(s)
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelectedUsers([])}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-white transition"
            >
              Désélectionner
            </button>
            {canResetPassword ? (
              <button
                type="button"
                onClick={() => openResetPasswordModal(selectedUsers)}
                className="px-3 py-1.5 text-xs font-medium text-white bg-sky-600 rounded-lg hover:bg-sky-700 transition"
              >
                Réinitialiser les mots de passe
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleBulkDelete}
              className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
            >
              Supprimer la sélection
            </button>
          </div>
        </div>
      )}

      {/* Mobile Cards View */}
      <div className="md:hidden space-y-3 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {renderUsersPagination('top')}
        <div className="space-y-3 px-0 pt-0">
        {filteredUsers.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-2">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                aria-label="Sélectionner tous les utilisateurs visibles"
                checked={
                  filteredUsers.length > 0 &&
                  filteredUsers.every((u) => selectedUsers.includes(u.id))
                }
                onChange={(e) => {
                  e.stopPropagation();
                  toggleSelectAllUsers();
                }}
                className="h-4 w-4 text-entrea-royal focus:ring-entrea-royal/40 border-gray-300 rounded"
              />
              Tout sélectionner
            </label>
          </div>
        )}
        {filteredUsers.map((user) => (
          <div
            key={user.id}
            className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm"
            onClick={() => navigate(`${usersBasePath}/${user.id}`)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <label className="inline-flex items-center gap-2 mb-2">
                  <input
                    type="checkbox"
                    aria-label={`Sélectionner l'utilisateur ${user.username || user.id}`}
                    checked={selectedUsers.includes(user.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleUserSelection(user.id);
                    }}
                    className="h-4 w-4 text-entrea-royal focus:ring-entrea-royal/40 border-gray-300 rounded"
                  />
                  <span className="text-xs text-gray-500">Sélectionner</span>
                </label>
                <h3 className="text-base font-bold text-gray-900 mb-1">{user.username || '-'}</h3>
                <p className="text-xs text-gray-500 mb-2">{user.phone || '-'}</p>
                {user.email && (
                  <p className="text-xs text-gray-500 mb-2 break-all">{user.email}</p>
                )}
              </div>
              <span className={`ml-3 px-2.5 py-1 text-xs font-semibold rounded-full flex-shrink-0 ${getRoleBadgeColor(user.role)}`}>
                {roleColumnLabelForUser(user)}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Membre lié</p>
                <p className="text-sm font-semibold text-gray-900 truncate">{getMemberName(user.member_id)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Statut</p>
                <p className={`text-sm font-semibold ${user.is_active ? 'text-green-600' : 'text-red-600'}`}>
                  {user.is_active ? 'Actif' : 'Inactif'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end mt-3 pt-3 border-t border-gray-100">
              <TableRowActions
                onEdit={() => {
                  setEditingUser(user);
                  setFormData({
                    name: user.name || '',
                    phone: user.phone || '',
                    username: user.username || '',
                    email: user.email || '',
                    password: '',
                    role: normalizeUserRoleForForm(user.role) || 'participant',
                    member_id: user.member_id || '',
                    position_id: user.position_id || '',
                    is_active: user.is_active !== undefined ? user.is_active : true
                  });
                  setShowModal(true);
                }}
                onPasswordReset={canResetPassword ? () => openResetPasswordModal([user.id]) : undefined}
                onDelete={() => handleDelete(user.id)}
              />
            </div>
          </div>
        ))}
        {filteredUsers.length === 0 && users.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">Aucun utilisateur trouvé avec ces critères</p>
          </div>
        )}
        {users.length === 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-500 text-sm">Aucun utilisateur trouvé</p>
          </div>
        )}
        </div>
        {renderUsersPagination('bottom')}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {renderUsersPagination('top')}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label="Sélectionner tous les utilisateurs visibles"
                    checked={
                      filteredUsers.length > 0 &&
                      filteredUsers.every((u) => selectedUsers.includes(u.id))
                    }
                    onChange={toggleSelectAllUsers}
                    className="h-4 w-4 text-entrea-royal focus:ring-entrea-royal/40 border-gray-300 rounded"
                  />
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom Utilisateur
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Téléphone
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rôle
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Membre lié
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date de création
                </th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => (
                <tr 
                  key={user.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={(e) => {
                    // Ne pas naviguer si on clique sur un bouton ou une checkbox
                    if (e.target.tagName !== 'BUTTON' && e.target.tagName !== 'INPUT') {
                      navigate(`${usersBasePath}/${user.id}`);
                    }
                  }}
                >
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      aria-label={`Sélectionner l'utilisateur ${user.username || user.id}`}
                      checked={selectedUsers.includes(user.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleUserSelection(user.id);
                      }}
                      className="h-4 w-4 text-entrea-royal focus:ring-entrea-royal/40 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 hover:text-entrea-royal">{user.username || '-'}</div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{user.phone || '-'}</div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{user.email || '-'}</div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
                      {roleColumnLabelForUser(user)}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{getMemberName(user.member_id)}</div>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <TableRowActions
                      onEdit={() => navigate(`${usersBasePath}/${user.id}/edit`)}
                      onPasswordReset={canResetPassword ? () => openResetPasswordModal([user.id]) : undefined}
                      onDelete={() => handleDelete(user.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && users.length > 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucun utilisateur trouvé avec ces critères
            </div>
          )}
          {users.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucun utilisateur trouvé
            </div>
          )}
        </div>
        {renderUsersPagination('bottom')}
      </div>

      {resetPwModalOpen && (
        <ModalShell maxWidthClass="max-w-lg" onBackdropClick={() => !resetPwSaving && closeResetPasswordModal()}>
          <div className="p-4 sm:p-6">
            {resetPwStep === 'form' ? (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-1">Réinitialiser le mot de passe</h2>
                <p className="text-sm text-gray-600 mb-4">
                  {resetPwTargets.length} compte(s) sélectionné(s). Les utilisateurs concernés pourront se connecter avec
                  le nouveau mot de passe
                  {resetPwMustChange ? ' et devront le changer si vous imposez la modification à la prochaine connexion.' : '.'}
                </p>
                <form onSubmit={handleSubmitResetPassword} className="space-y-4">
                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={resetPwShared}
                      onChange={(e) => setResetPwShared(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-entrea-royal border-gray-300 rounded"
                    />
                    <span>
                      Définir un <strong>mot de passe commun</strong> pour tous les comptes sélectionnés (sinon un mot de
                      passe provisoire <strong>distinct</strong> est généré pour chacun).
                    </span>
                  </label>
                  {resetPwShared ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe *</label>
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={resetPwSharedValue}
                          onChange={(e) => setResetPwSharedValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                          minLength={8}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer *</label>
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={resetPwSharedConfirm}
                          onChange={(e) => setResetPwSharedConfirm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                          minLength={8}
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                      Après validation, copiez les mots de passe provisoires depuis l’écran de synthèse et transmettez-les
                      par un canal sécurisé.
                    </p>
                  )}
                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      checked={resetPwMustChange}
                      onChange={(e) => setResetPwMustChange(e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-entrea-royal border-gray-300 rounded"
                    />
                    <span>
                      Forcer le changement de mot de passe à la <strong>prochaine connexion</strong>
                    </span>
                  </label>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      disabled={resetPwSaving}
                      onClick={closeResetPasswordModal}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition text-sm"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={resetPwSaving}
                      className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition text-sm disabled:opacity-60"
                    >
                      {resetPwSaving ? 'Application…' : 'Réinitialiser'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-gray-900 mb-3">Réinitialisation effectuée</h2>
                {resetPwOutcome?.truncated ? (
                  <p className="text-sm text-amber-800 mb-2">
                    Attention : la liste envoyée au serveur était trop longue ; seuls les premiers comptes ont été traités.
                  </p>
                ) : null}
                <div className="max-h-56 overflow-auto rounded-lg border border-gray-200 mb-3 text-sm">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Utilisateur</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Mot de passe provisoire</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">État</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {(resetPwOutcome?.results || []).map((r) => {
                        const u = users.find((x) => String(x.id) === String(r.user_id));
                        const label = u?.username || u?.name || r.user_id;
                        return (
                          <tr key={String(r.user_id)}>
                            <td className="px-3 py-2 font-medium text-gray-900">{label}</td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-800">
                              {r.temporary_password ||
                                (resetPwOutcome?.used_shared_password ? '— (commun, non affiché)' : '—')}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {r.ok ? (
                                <span className="text-green-700">OK</span>
                              ) : (
                                <span className="text-red-700">{r.error || 'Erreur'}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={copyResetPasswordSummary}
                    className="px-4 py-2 border border-gray-300 text-gray-800 rounded-lg hover:bg-gray-50 transition text-sm"
                  >
                    Copier le récapitulatif
                  </button>
                  <button
                    type="button"
                    onClick={closeResetPasswordModal}
                    className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition text-sm"
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </ModalShell>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <ModalShell maxWidthClass="max-w-md" onBackdropClick={handleCloseModal}>
          <div className="p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
                {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                {(() => {
                  const isMember = isMemberEffectiveRole(formData.role) || formData.member_id;
                  
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom Utilisateur {!isMember && '*'}
                        {isMember && (
                          <span className="text-xs text-gray-500 font-normal ml-1">(sera le numéro de téléphone si vide)</span>
                        )}
                      </label>
                      <input
                        type="text"
                        required={!isMember}
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                        placeholder={isMember ? 'Sera le numéro de téléphone si vide' : ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                      />
                    </div>
                  );
                })()}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numéro de téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+250788123456"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email (optionnel)
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  />
                </div>

                {(() => {
                  const isMember = isMemberEffectiveRole(formData.role) || formData.member_id;
                  const isPasswordRequired = !editingUser && !isMember;
                  
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mot de passe {isPasswordRequired && '*'}
                        {!editingUser && isMember && (
                          <span className="text-xs text-gray-500 font-normal ml-1">(sera généré automatiquement)</span>
                        )}
                      </label>
                      <input
                        type="password"
                        required={isPasswordRequired}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder={
                          editingUser 
                            ? 'Laisser vide pour ne pas changer' 
                            : isMember 
                              ? 'Généré automatiquement' 
                              : ''
                        }
                        disabled={!editingUser && isMember}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </div>
                  );
                })()}

                {editingTechnicalIam ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rôle IAM (console) *
                    </label>
                    <select
                      required
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                    >
                      {assignableRoles.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.label}
                        </option>
                      ))}
                      {formData.role &&
                        !assignableRoles.some((r) => r.key === formData.role) && (
                          <option value={formData.role}>{formatIamRoleLabel(formData.role)}</option>
                        )}
                    </select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">
                      Rôle IAM affiché :{' '}
                      <span className="font-medium text-gray-800">{formatIamRoleLabel(formData.role)}</span>
                      {' — '}les droits métier Entrea viennent de la{' '}
                      <strong>position</strong> (config. console admin).
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Position projet</label>
                  <p className="text-xs text-gray-500 mb-1">
                    {editingTechnicalIam
                      ? 'Même pour un compte technique, vous pouvez lier une position sur ce projet si l’utilisateur a aussi un usage métier.'
                      : null}
                  </p>
                  <select
                    value={formData.position_id || ''}
                    onChange={(e) => {
                      const nextPid = e.target.value;
                      const derivedRole = nextPid
                        ? resolveUserRoleFromPosition(nextPid, projectPositions, formData.role || 'participant')
                        : null;
                      setFormData((prev) => ({
                        ...prev,
                        position_id: nextPid,
                        ...(derivedRole && !editingTechnicalIam ? { role: derivedRole } : {}),
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    <option value="">— Aucune —</option>
                    {projectPositions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  {(() => {
                    const pid = String(formData.position_id || '').trim();
                    if (!pid) return null;
                    const pos = projectPositions.find((p) => String(p?.id || '').trim() === pid);
                    const primary = primaryRoleKeyFromPosition(pos);
                    if (!primary) return null;
                    return (
                      <p className="mt-1 text-xs text-gray-500">
                        Cette position confère le rôle{' '}
                        <span className="font-semibold text-entrea-royal">
                          {formatIamRoleLabel(primary)}
                        </span>{' '}
                        <span className="font-mono text-gray-500">({primary})</span>.
                      </p>
                    );
                  })()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Membre lié (optionnel)
                  </label>
                  <select
                    value={formData.member_id}
                    onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-entrea-royal/40 focus:border-entrea-sky"
                  >
                    <option value="">Aucun membre</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-entrea-royal focus:ring-entrea-royal/40 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                    Compte actif
                  </label>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium text-sm"
                  >
                    {editingUser ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default UsersManagement;

